// src/components/JobMoneyPanel.js
//
// "Job Money" — one place to see and track the money on a job.
//
//   Original contract (approved proposal or the job's own total, read-only)
//   + change orders            (tracked here, can be voided, never deleted)
//   ± total adjustments        (set a new total with a reason; proposal untouched)
//   = current contract total   (what deposit / progress / final invoices bill against)
//   billed / paid / left to bill / still owed
//   linked work orders         (combined total only; never change this balance)
//
// Collapsed by default and only reads Firestore while open. Data lives in
// jobContracts/{contractId} — see utils/jobContractLedger.js.
//
//   <JobMoneyPanel job={selectedJob} canEdit />

import React, { useCallback, useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  BILLING_KIND_LABEL,
  formatMoney,
  getContractIdForJob,
  getJobTotalForBilling,
  getProposalContractTotal,
  getRefreshedHeaderPatch,
  loadContractInvoices,
} from "../utils/billingFlow";
import {
  addChangeOrder,
  addTotalAdjustment,
  combineTotals,
  correctOriginalTotal,
  ledgerOwnsTotal,
  syncOriginalIfFollowing,
  computeContractTotals,
  ensureContractLedger,
  findContractsLinkingWO,
  loadContractLedger,
  loadInvoicesForWO,
  loadJobsForWO,
  loadPaymentsForWOs,
  money,
  normalizeWOs,
  pushNewTotalToOpenInvoices,
  r2,
  setLinkedWOs,
  voidLedgerEntry,
} from "../utils/jobContractLedger";
import { findInvoicesByWO, invoiceOptionLabel, loadSiteInvoices, tagInvoice } from "../utils/invoiceTagging";
import "./JobMoneyPanel.css";

const today = () => new Date().toISOString().slice(0, 10);
const fmt = (n) => formatMoney(n);
const signed = (n) => (money(n) > 0 ? `+${fmt(n)}` : fmt(n));
const dateText = (d) => (d ? String(d).slice(0, 10) : "");

const invoiceLabel = (inv) => {
  const base = BILLING_KIND_LABEL[inv.billingKind] || "Invoice";
  return inv.billingKind === "progress" && inv.billingSequence ? `${base} ${inv.billingSequence}` : base;
};

/* The original total while Job Money does not own the total yet: exactly
   the number billing uses today (so it always matches Add / Change Total).
   Labelled "approved proposal" when it still equals the proposal's total. */
const findOriginalTotal = async (job) => {
  const total = await getJobTotalForBilling(job);
  const proposalId = job?.proposalId || job?.contractBilling?.proposalId;
  if (proposalId) {
    try {
      const snap = await getDoc(doc(db, "proposals", proposalId));
      const proposalTotal = snap.exists() ? getProposalContractTotal(snap.data()) : 0;
      if (!total && proposalTotal) return { total: proposalTotal, source: "proposal" };
      if (proposalTotal && r2(proposalTotal) === r2(total)) return { total, source: "proposal" };
    } catch (err) {
      console.warn("Proposal total lookup failed:", err);
    }
  }
  return { total, source: "job" };
};

/* Total / billed / paid for one linked work order. */
const loadLinkedWO = async (wo) => {
  const [jobs, invoices, payments] = await Promise.all([
    loadJobsForWO(wo),
    loadInvoicesForWO(wo),
    loadPaymentsForWOs([wo]),
  ]);
  const job = jobs[0] || null;
  const total = job ? await getJobTotalForBilling(job) : r2(invoices.reduce((s, i) => s + money(i.amount), 0));
  return {
    wo,
    found: !!job || invoices.length > 0,
    siteName: job?.siteName || invoices[0]?.siteName || "",
    scope: job?.scope || invoices[0]?.scope || "",
    status: job?.status || "",
    total: r2(total),
    billed: r2(invoices.reduce((s, i) => s + money(i.amount ?? i.rate), 0)),
    paid: r2(payments.reduce((s, p) => s + money(p.amount), 0)),
  };
};

export default function JobMoneyPanel({ job, canEdit = false, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const [coForm, setCoForm] = useState({ description: "", amount: "", date: today(), reference: "" });
  const [adjForm, setAdjForm] = useState({ newTotal: "", reason: "" });
  const [showAdjust, setShowAdjust] = useState(false);
  const [origForm, setOrigForm] = useState({ newOriginal: "", reason: "" });
  const [showCorrect, setShowCorrect] = useState(false);
  const [woInput, setWoInput] = useState("");
  // pick = an invoice id from this site's list, or "__other__" to type a number
  const [attachForm, setAttachForm] = useState({ pick: "", wo: "", kind: "progress" });
  const [showVoided, setShowVoided] = useState(false);

  const contractId = job ? getContractIdForJob(job) : "";

  const load = useCallback(async () => {
    if (!job?.id) return;
    setLoading(true);
    setError("");
    try {
      const ledger = await loadContractLedger(contractId);
      // Until Job Money owns the total (change order, adjustment or a
      // corrected original), the original follows the job's live total, so
      // Add Total / Change Total keep working.
      const original = ledger && ledgerOwnsTotal(ledger) ? null : await findOriginalTotal(job);

      // Invoices on this contract, plus any for this WO that predate it.
      const [contractInvoices, woInvoices] = await Promise.all([
        loadContractInvoices(contractId),
        job.wo ? loadInvoicesForWO(job.wo) : Promise.resolve([]),
      ]);
      const byId = {};
      [...contractInvoices, ...woInvoices].forEach((inv) => {
        byId[inv.id] = inv;
      });
      const invoices = Object.values(byId).sort((a, b) =>
        String(a.invoiceDate || "").localeCompare(String(b.invoiceDate || ""))
      );

      const payWOs = normalizeWOs([job.wo, ...invoices.map((i) => i.wo)]);
      const payments = await loadPaymentsForWOs(payWOs);

      const linkedWOs = normalizeWOs(ledger?.linkedWOs || []).filter(
        (wo) => !payWOs.map((w) => w.toUpperCase()).includes(wo.toUpperCase())
      );
      const [linked, linkedFrom, siteInvoices] = await Promise.all([
        Promise.all(linkedWOs.map(loadLinkedWO)),
        job.wo ? findContractsLinkingWO(job.wo) : Promise.resolve([]),
        loadSiteInvoices(job.siteName),
      ]);

      setData({
        ledger,
        original,
        invoices,
        payments,
        linked,
        linkedFrom: linkedFrom.filter((c) => c.contractId !== contractId),
        siteInvoices,
      });
    } catch (err) {
      console.error("Job Money load failed:", err);
      setError("Could not load this job's money. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [job, contractId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!job?.id) return null;

  // ---- numbers ----------------------------------------------------------
  const ledger = data?.ledger || null;
  const owns = !!ledger && ledgerOwnsTotal(ledger);
  const totals = computeContractTotals(
    ledger && !owns ? { ...ledger, originalTotal: data?.original?.total || 0 } : ledger,
    data?.original?.total || 0
  );
  const billed = r2((data?.invoices || []).reduce((s, i) => s + money(i.amount ?? i.rate), 0));
  const paid = r2((data?.payments || []).reduce((s, p) => s + money(p.amount), 0));
  const own = { total: totals.current, billed, paid };
  const combined = combineTotals(own, data?.linked || []);
  const originalSource = ledger?.originalSource || data?.original?.source || "job";

  // ---- writes -----------------------------------------------------------
  // Existing link-only ledgers take today's original before it is fixed.
  const withLedger = async () =>
    ledger
      ? syncOriginalIfFollowing(ledger, data?.original?.total || 0)
      : ensureContractLedger({
          contractId,
          job,
          originalTotal: data?.original?.total || 0,
          originalSource: data?.original?.source || "job",
        });

  const afterTotalChange = async (next) => {
    const newTotal = computeContractTotals(next).current;
    const invoices = await loadContractInvoices(contractId);
    const changed = await pushNewTotalToOpenInvoices({
      contractId,
      newTotal,
      invoices,
      refreshHeader: getRefreshedHeaderPatch,
    });
    return changed;
  };

  const run = async (fn) => {
    setSaving(true);
    setError("");
    try {
      await fn();
      await load();
    } catch (err) {
      console.error("Job Money save failed:", err);
      setError("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const submitChangeOrder = () => {
    if (!coForm.description.trim()) return setError("Describe the change order.");
    if (!money(coForm.amount)) return setError("Enter the change order amount (use a minus sign for a credit).");
    run(async () => {
      const base = await withLedger();
      const next = await addChangeOrder(base, coForm);
      const changed = await afterTotalChange(next);
      setCoForm({ description: "", amount: "", date: today(), reference: "" });
      if (changed) alert(`${changed} open invoice(s) on this job now show the new total.`);
    });
  };

  const submitAdjustment = () => {
    if (adjForm.newTotal === "" || Number.isNaN(Number(adjForm.newTotal))) {
      return setError("Enter the new total.");
    }
    if (!adjForm.reason.trim()) return setError("A reason is required to change the total.");
    if (r2(money(adjForm.newTotal)) === totals.current) return setError("That is already the current total.");
    const ok = window.confirm(
      `Change this job's total from ${fmt(totals.current)} to ${fmt(money(adjForm.newTotal))}?\n\n` +
        "The approved proposal is not changed. Sent and paid invoices are not changed."
    );
    if (!ok) return;
    run(async () => {
      const base = await withLedger();
      const next = await addTotalAdjustment(base, adjForm);
      await afterTotalChange(next);
      setAdjForm({ newTotal: "", reason: "" });
      setShowAdjust(false);
    });
  };

  const submitOriginalCorrection = () => {
    if (origForm.newOriginal === "" || Number.isNaN(Number(origForm.newOriginal))) {
      return setError("Enter the correct original total.");
    }
    if (!origForm.reason.trim()) return setError("A reason is required to correct the original total.");
    const to = r2(money(origForm.newOriginal));
    if (to === totals.original) return setError("That is already the original total.");
    const newCurrent = r2(to + totals.changeOrders + totals.adjustments);
    const ok = window.confirm(
      `Correct the ORIGINAL contract total from ${fmt(totals.original)} to ${fmt(to)}?\n\n` +
        (totals.changeOrders || totals.adjustments
          ? `Change orders and adjustments stay on top, so the current total becomes ${fmt(newCurrent)}.\n\n`
          : "") +
        "This is logged with your reason. The approved proposal is not changed, and sent or paid invoices are not changed."
    );
    if (!ok) return;
    run(async () => {
      const base = await withLedger();
      const next = await correctOriginalTotal(base, origForm);
      await afterTotalChange(next);
      setOrigForm({ newOriginal: "", reason: "" });
      setShowCorrect(false);
    });
  };

  const voidEntry = (listKey, entry) => {
    const label = listKey === "changeOrders" ? entry.number : "this adjustment";
    const reason = window.prompt(`Void ${label}? It stays on record as voided.\n\nReason (optional):`, "");
    if (reason === null) return;
    run(async () => {
      const next = await voidLedgerEntry(ledger, listKey, entry.id, reason);
      await afterTotalChange(next);
    });
  };

  // Put an invoice that already exists (older, or typed in by hand) on this
  // job's balance as a deposit / progress / final / standard invoice.
  const attachInvoice = () => {
    const kind = attachForm.kind;
    if (!attachForm.pick) return setError("Pick an invoice to add.");
    run(async () => {
      let target = null;
      if (attachForm.pick === "__other__") {
        const wo = attachForm.wo.trim();
        if (!wo) {
          setError("Type the invoice # (WO) to add.");
          return;
        }
        const found = await findInvoicesByWO(wo);
        if (!found.length) {
          setError(`No invoice found with invoice # ${wo}.`);
          return;
        }
        if (found.length > 1) {
          setError(
            `${found.length} invoices use ${wo} (${found.map((i) => `${i.wo} ${fmt(i.amount)}`).join(", ")}). ` +
              "Open the right one on Approved Invoices and set it there."
          );
          return;
        }
        target = found[0];
      } else {
        target = (data?.siteInvoices || []).find((inv) => inv.id === attachForm.pick);
      }
      if (!target) {
        setError("That invoice could not be found. Refresh and try again.");
        return;
      }
      const result = await tagInvoice(target, { kind, job });
      if (result.error) {
        setError(result.error);
        return;
      }
      setAttachForm({ pick: "", wo: "", kind });
    });
  };

  // Take an invoice that was added here by hand back off this job's balance.
  const detachInvoice = (inv) => {
    if (!window.confirm(`Remove invoice ${inv.wo || ""} from this job's balance? The invoice itself is not changed.`)) return;
    run(async () => {
      const result = await tagInvoice(inv, { kind: "standard", jobWO: "" });
      if (result.error) setError(result.error);
    });
  };

  const addLinkedWO = () => {
    const wo = woInput.trim();
    if (!wo) return;
    if (job.wo && wo.toUpperCase() === String(job.wo).toUpperCase()) {
      return setError("That is this job's own work order.");
    }
    run(async () => {
      const base = await withLedger();
      await setLinkedWOs(base, [...(base.linkedWOs || []), wo]);
      setWoInput("");
    });
  };

  const removeLinkedWO = (wo) => {
    if (!window.confirm(`Unlink WO ${wo} from this job?`)) return;
    run(async () => {
      await setLinkedWOs(
        ledger,
        (ledger.linkedWOs || []).filter((w) => w.toUpperCase() !== wo.toUpperCase())
      );
    });
  };

  const changeOrders = (ledger?.changeOrders || []).filter((c) => showVoided || !c.voided);
  const adjustments = (ledger?.adjustments || []).filter((a) => showVoided || !a.voided);
  const hasVoided =
    (ledger?.changeOrders || []).some((c) => c.voided) || (ledger?.adjustments || []).some((a) => a.voided);

  return (
    <div className="jm-panel" onClick={(e) => e.stopPropagation()}>
      <button type="button" className="jm-toggle" onClick={() => setOpen((v) => !v)}>
        {open ? "▾" : "▸"} 💵 Job Money
        {open && data ? <span className="jm-toggle-sub"> · {fmt(totals.current)} contract</span> : null}
      </button>

      {open && (
        <div className="jm-body">
          {loading && !data && <div className="jm-muted">Loading…</div>}
          {error && <div className="jm-error">{error}</div>}

          {data && (
            <>
              {/* ---------------- Contract ---------------- */}
              <div className="jm-summary">
                <div className="jm-line">
                  <span>
                    Original contract{" "}
                    <em className="jm-muted">
                      ({originalSource === "proposal" ? "approved proposal" : "job total"})
                    </em>
                  </span>
                  <b>{fmt(totals.original)}</b>
                </div>
                {(ledger?.originalHistory || []).length > 0 && (
                  <ul className="jm-list jm-small">
                    {ledger.originalHistory.map((h) => (
                      <li key={h.id}>
                        Original corrected {fmt(h.from)} → <b>{fmt(h.to)}</b> · {h.reason}{" "}
                        <span className="jm-muted">· {h.by || "—"} · {dateText(h.at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {canEdit &&
                  (showCorrect ? (
                    <div className="jm-form">
                      <input
                        type="number"
                        step="0.01"
                        placeholder={`Correct original (now ${fmt(totals.original)})`}
                        value={origForm.newOriginal}
                        onChange={(e) => setOrigForm((f) => ({ ...f, newOriginal: e.target.value }))}
                      />
                      <input
                        type="text"
                        placeholder="Reason (required, e.g. typo on proposal)"
                        value={origForm.reason}
                        onChange={(e) => setOrigForm((f) => ({ ...f, reason: e.target.value }))}
                      />
                      <button type="button" className="jm-btn primary" disabled={saving} onClick={submitOriginalCorrection}>
                        Save correction
                      </button>
                      <button type="button" className="jm-btn" onClick={() => setShowCorrect(false)}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button type="button" className="jm-link" onClick={() => setShowCorrect(true)}>
                      Correct original total…
                    </button>
                  ))}
                <div className="jm-line">
                  <span>Change orders ({totals.changeOrderCount})</span>
                  <b>{signed(totals.changeOrders)}</b>
                </div>
                {totals.adjustments !== 0 && (
                  <div className="jm-line">
                    <span>Total adjustments</span>
                    <b>{signed(totals.adjustments)}</b>
                  </div>
                )}
                <div className="jm-line jm-strong">
                  <span>Current contract total</span>
                  <b>{fmt(totals.current)}</b>
                </div>
                <div className="jm-grid">
                  <div><span>Billed</span><b>{fmt(billed)}</b></div>
                  <div><span>Paid</span><b>{fmt(paid)}</b></div>
                  <div><span>Left to bill</span><b>{fmt(r2(totals.current - billed))}</b></div>
                  <div><span>Still owed</span><b className={billed - paid > 0 ? "jm-owed" : ""}>{fmt(r2(billed - paid))}</b></div>
                </div>
              </div>

              {/* ---------------- Change orders ---------------- */}
              <h4 className="jm-h">Change orders</h4>
              {changeOrders.length === 0 ? (
                <div className="jm-muted">None yet.</div>
              ) : (
                <table className="jm-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th className="num">Amount</th>
                      {canEdit && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {changeOrders.map((c) => (
                      <tr key={c.id} className={c.voided ? "jm-voided" : ""}>
                        <td>{c.number}</td>
                        <td>{dateText(c.date)}</td>
                        <td>
                          {c.description}
                          {c.reference ? <div className="jm-muted">Ref: {c.reference}</div> : null}
                          <div className="jm-muted">
                            Added by {c.addedBy || "—"}
                            {c.voided ? ` · VOIDED by ${c.voidedBy || "—"}${c.voidReason ? `: ${c.voidReason}` : ""}` : ""}
                          </div>
                        </td>
                        <td className="num">{signed(c.amount)}</td>
                        {canEdit && (
                          <td>
                            {!c.voided && (
                              <button type="button" className="jm-link" disabled={saving} onClick={() => voidEntry("changeOrders", c)}>
                                Void
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {canEdit && (
                <div className="jm-form">
                  <input
                    type="text"
                    placeholder="What changed (e.g. Replace 3 extra rotted joists)"
                    value={coForm.description}
                    onChange={(e) => setCoForm((f) => ({ ...f, description: e.target.value }))}
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Amount (− for credit)"
                    value={coForm.amount}
                    onChange={(e) => setCoForm((f) => ({ ...f, amount: e.target.value }))}
                  />
                  <input
                    type="date"
                    value={coForm.date}
                    onChange={(e) => setCoForm((f) => ({ ...f, date: e.target.value }))}
                  />
                  <input
                    type="text"
                    placeholder="Reference (optional: who approved, PO #)"
                    value={coForm.reference}
                    onChange={(e) => setCoForm((f) => ({ ...f, reference: e.target.value }))}
                  />
                  <button type="button" className="jm-btn primary" disabled={saving} onClick={submitChangeOrder}>
                    {saving ? "Saving…" : "Add change order"}
                  </button>
                </div>
              )}

              {/* ---------------- Total adjustments ---------------- */}
              {(adjustments.length > 0 || canEdit) && <h4 className="jm-h">Total adjustments</h4>}
              {adjustments.length > 0 && (
                <ul className="jm-list">
                  {adjustments.map((a) => (
                    <li key={a.id} className={a.voided ? "jm-voided" : ""}>
                      {dateText(a.date)} · {fmt(a.fromTotal)} → <b>{fmt(a.toTotal)}</b> ({signed(a.amount)}) ·{" "}
                      {a.reason} <span className="jm-muted">· {a.addedBy || "—"}</span>
                      {a.voided ? <span className="jm-muted"> · VOIDED</span> : null}
                      {canEdit && !a.voided && (
                        <button type="button" className="jm-link" disabled={saving} onClick={() => voidEntry("adjustments", a)}>
                          Void
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {canEdit &&
                (showAdjust ? (
                  <div className="jm-form">
                    <input
                      type="number"
                      step="0.01"
                      placeholder={`New total (now ${fmt(totals.current)})`}
                      value={adjForm.newTotal}
                      onChange={(e) => setAdjForm((f) => ({ ...f, newTotal: e.target.value }))}
                    />
                    <input
                      type="text"
                      placeholder="Reason (required)"
                      value={adjForm.reason}
                      onChange={(e) => setAdjForm((f) => ({ ...f, reason: e.target.value }))}
                    />
                    <button type="button" className="jm-btn primary" disabled={saving} onClick={submitAdjustment}>
                      Save new total
                    </button>
                    <button type="button" className="jm-btn" onClick={() => setShowAdjust(false)}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button type="button" className="jm-btn" onClick={() => setShowAdjust(true)}>
                    Change total…
                  </button>
                ))}
              {canEdit && (
                <div className="jm-muted jm-small">
                  Use <b>Change total…</b> for a real change to the job (discount, scope cut). Use{" "}
                  <b>Correct original total…</b> when the original number itself was wrong. Neither edits
                  the approved proposal; draft invoices pick up the new total, sent and paid invoices stay
                  as they are.
                </div>
              )}
              {hasVoided && (
                <button type="button" className="jm-link" onClick={() => setShowVoided((v) => !v)}>
                  {showVoided ? "Hide voided" : "Show voided"}
                </button>
              )}

              {/* ---------------- Invoices ---------------- */}
              <h4 className="jm-h">Invoices on this job</h4>
              {data.invoices.length === 0 ? (
                <div className="jm-muted">No invoices yet.</div>
              ) : (
                <table className="jm-table">
                  <thead>
                    <tr>
                      <th>WO</th>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th className="num">Amount</th>
                      {canEdit && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {data.invoices.map((inv) => (
                      <tr key={inv.id}>
                        <td>{inv.wo || "—"}</td>
                        <td>{dateText(inv.invoiceDate)}</td>
                        <td>{invoiceLabel(inv)}</td>
                        <td>{inv.status || "Draft"}</td>
                        <td className="num">{fmt(inv.amount ?? inv.rate)}</td>
                        {canEdit && (
                          <td>
                            {inv.contractId === contractId && inv.sourceJobId !== job.id && (
                              <button type="button" className="jm-link" disabled={saving} onClick={() => detachInvoice(inv)}>
                                Remove
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {canEdit && (
                <div className="jm-form">
                  <select
                    className="jm-select jm-select-wide"
                    value={attachForm.pick}
                    onChange={(e) => setAttachForm((f) => ({ ...f, pick: e.target.value }))}
                  >
                    <option value="">Add an invoice from {job.siteName || "this site"}…</option>
                    {(data.siteInvoices || [])
                      .filter((inv) => !data.invoices.some((own) => own.id === inv.id))
                      .map((inv) => (
                        <option key={inv.id} value={inv.id}>
                          {invoiceOptionLabel(inv, fmt)}
                        </option>
                      ))}
                    <option value="__other__">Other invoice # (another site)…</option>
                  </select>
                  {attachForm.pick === "__other__" && (
                    <input
                      type="text"
                      placeholder="Invoice # (e.g. PJMC-3041-2)"
                      value={attachForm.wo}
                      onChange={(e) => setAttachForm((f) => ({ ...f, wo: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          attachInvoice();
                        }
                      }}
                    />
                  )}
                  <select
                    className="jm-select"
                    value={attachForm.kind}
                    onChange={(e) => setAttachForm((f) => ({ ...f, kind: e.target.value }))}
                  >
                    <option value="deposit">as Deposit</option>
                    <option value="progress">as Progress payment</option>
                    <option value="final">as Final payment</option>
                    <option value="standard">as Standard</option>
                  </select>
                  <button type="button" className="jm-btn" disabled={saving || !attachForm.pick} onClick={attachInvoice}>
                    Add existing invoice
                  </button>
                </div>
              )}

              {/* ---------------- Linked work orders ---------------- */}
              <h4 className="jm-h">Linked work orders</h4>
              <div className="jm-muted jm-small">
                Related jobs tallied into the combined total below. They keep their own invoices and
                never change this job's balance.
              </div>
              {data.linked.length > 0 && (
                <table className="jm-table">
                  <thead>
                    <tr>
                      <th>WO</th>
                      <th>Site / scope</th>
                      <th className="num">Total</th>
                      <th className="num">Billed</th>
                      <th className="num">Paid</th>
                      {canEdit && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {data.linked.map((l) => (
                      <tr key={l.wo}>
                        <td>{l.wo}</td>
                        <td>
                          {l.found ? (
                            <>
                              {l.siteName || "—"}
                              {l.scope ? <div className="jm-muted">{String(l.scope).replace(/\(\([a-z-]\)\)/gi, "").slice(0, 80)}</div> : null}
                            </>
                          ) : (
                            <span className="jm-warn">No job or invoice found for this WO</span>
                          )}
                        </td>
                        <td className="num">{fmt(l.total)}</td>
                        <td className="num">{fmt(l.billed)}</td>
                        <td className="num">{fmt(l.paid)}</td>
                        {canEdit && (
                          <td>
                            <button type="button" className="jm-link" disabled={saving} onClick={() => removeLinkedWO(l.wo)}>
                              Unlink
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {canEdit && (
                <div className="jm-form">
                  <input
                    type="text"
                    placeholder="WO number to link (e.g. 4410)"
                    value={woInput}
                    onChange={(e) => setWoInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addLinkedWO();
                      }
                    }}
                  />
                  <button type="button" className="jm-btn" disabled={saving} onClick={addLinkedWO}>
                    Link WO
                  </button>
                </div>
              )}

              {data.linked.length > 0 && (
                <div className="jm-summary jm-combined">
                  <div className="jm-line jm-strong">
                    <span>Combined total (this job + {data.linked.length} linked)</span>
                    <b>{fmt(combined.total)}</b>
                  </div>
                  <div className="jm-grid">
                    <div><span>Billed</span><b>{fmt(combined.billed)}</b></div>
                    <div><span>Paid</span><b>{fmt(combined.paid)}</b></div>
                    <div><span>Left to bill</span><b>{fmt(combined.leftToBill)}</b></div>
                    <div><span>Still owed</span><b>{fmt(combined.owed)}</b></div>
                  </div>
                </div>
              )}

              {data.linkedFrom.length > 0 && (
                <div className="jm-muted jm-small" style={{ marginTop: 8 }}>
                  This WO is also linked from:{" "}
                  {data.linkedFrom.map((c) => `${c.ownerWO || c.contractId}${c.siteName ? ` (${c.siteName})` : ""}`).join(", ")}
                </div>
              )}

              <div className="jm-foot">
                <button type="button" className="jm-link" disabled={loading} onClick={load}>
                  {loading ? "Refreshing…" : "Refresh"}
                </button>
                <span className="jm-muted jm-small">
                  Paid = payments recorded on Deposits against this job's invoice numbers.
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
